import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Building2, Banknote, Radio, UserRound, Flame, Lock, Compass } from "lucide-react";
import Layout from "@/components/Layout";
import { Reveal, Overline } from "@/components/Reveal";
import { useSettings } from "@/context/SettingsContext";
import { MEDIA, VERTICALS, AUDIENCE } from "@/lib/content";

const VERT_ICONS = { capital: Banknote, media: Radio, cre: Building2, michael: UserRound, aspire: Flame };

export default function Home() {
  const { settings } = useSettings();
  const navigate = useNavigate();
  const c = settings.content;
  const r = settings.redirects;

  const openExternal = (key) => {
    const url = r?.[key];
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Layout>
      {/* 1. HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden grain" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={MEDIA.heroSkyline} alt="City skyline" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-24 w-full z-10">
          <Reveal>
            <Overline>Real Estate · Capital · Media · Ownership · Legacy</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight mt-5 max-w-4xl leading-[1.02]">
              {c.hero_headline}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-neutral-300 text-lg lg:text-xl mt-7 max-w-2xl leading-relaxed">{c.hero_subheadline}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <button onClick={() => navigate("/book-call")} className="btn-primary" data-testid="hero-book-call-btn">
                {c.primary_cta} <ArrowRight size={18} />
              </button>
              <button onClick={() => navigate("/private-network")} className="btn-ghost" data-testid="hero-network-btn">
                {c.secondary_cta}
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2. BRAND ECOSYSTEM */}
      <section className="py-24 max-w-7xl mx-auto px-6" data-testid="ecosystem-section">
        <Reveal>
          <Overline>The Ecosystem</Overline>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3 max-w-2xl">
            One command center. Five verticals of leverage.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {VERTICALS.map((v, i) => {
            const Icon = VERT_ICONS[v.key];
            return (
              <Reveal delay={i * 0.06} key={v.key}>
                <div className="card-edge p-8 h-full flex flex-col" data-testid={`vertical-card-${v.key}`}>
                  <Icon className="text-[#CC5500]" size={28} strokeWidth={1.5} />
                  <h3 className="font-display text-xl font-bold mt-5">{v.label}</h3>
                  <p className="text-neutral-400 text-sm mt-3 leading-relaxed flex-1">{v.desc}</p>
                  <div className="flex flex-wrap gap-3 mt-6">
                    <Link to={v.path} className="text-sm text-white inline-flex items-center gap-1 hover:text-[#CC5500] transition-colors" data-testid={`vertical-internal-${v.key}`}>
                      {v.cta} <ArrowRight size={15} />
                    </Link>
                    {v.redirectKey && (
                      <button onClick={() => openExternal(v.redirectKey)} className="text-sm text-neutral-500 inline-flex items-center gap-1 hover:text-white transition-colors" data-testid={`vertical-external-${v.key}`}>
                        Visit Site <ArrowUpRight size={15} />
                      </button>
                    )}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* 3. CHOOSE YOUR PATH */}
      <section className="py-24 bg-[#0E0E0E] border-y border-white/10" data-testid="audience-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Overline>Self-Selection</Overline>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Choose Your Path</h2>
            <p className="text-neutral-400 mt-3 max-w-xl">Tell us what you control and what you want. We'll route you to the right table.</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 mt-12 border border-white/10">
            {AUDIENCE.map((a, i) => (
              <Link
                key={a.label}
                to={a.path}
                className="bg-[#0A0A0A] hover:bg-[#1A1A1A] p-7 flex items-center justify-between group transition-colors"
                data-testid={`audience-${i}`}
              >
                <span className="font-display font-semibold text-lg">{a.label}</span>
                <Compass className="text-neutral-600 group-hover:text-[#CC5500] transition-colors" size={20} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 4. PRIVATE NETWORK */}
      <SplitSection
        image={MEDIA.network}
        overline="Private Network"
        title="A private network of owners, investors, operators & partners."
        body="Moaglee is building a curated network of property owners, investors, operators, sellers, and strategic partners — sharing private deal flow that never hits the open market."
        ctaLabel="Apply to Join the Private Network"
        to="/private-network"
        testid="private-network-section"
        reverse
      />

      {/* 5. CRE */}
      <SplitSection
        image={MEDIA.building}
        overline="Moaglee CRE"
        title="A private commercial real estate network."
        body="Commercial listings, a private seller network, buyer/investor requirements, tenant representation, off-market opportunities, and landlord advisory."
        ctaLabel="View CRE Opportunities"
        to="/cre"
        external={{ label: "MoagleeCRE.com", key: "cre" }}
        openExternal={openExternal}
        testid="cre-section"
      />

      {/* 6. CAPITAL */}
      <SplitSection
        image={MEDIA.network}
        overline="Moaglee Capital"
        title="Capital requests, private investors & creative finance."
        body="Capital requests, an investor network, sale-leasebacks, creative finance, private lending, and syndication opportunities — structured for outcomes."
        ctaLabel="Submit a Capital Request"
        to="/capital"
        external={{ label: "MoagleeCapital.com", key: "capital" }}
        openExternal={openExternal}
        testid="capital-section"
        reverse
      />

      {/* 7. MEDIA */}
      <SplitSection
        image={MEDIA.waterfront}
        overline="Moaglee Media"
        title="AI-powered marketing, CRM & automation systems."
        body="AI marketing systems, GHL automation, social content systems, CRM follow-up, inbound/outbound lead generation, and business owner visibility."
        ctaLabel="Build My AI Marketing System"
        to="/media"
        external={{ label: "MoagleeMedia.com", key: "media" }}
        openExternal={openExternal}
        testid="media-section"
      />

      {/* 8. MICHAEL BROWN */}
      <section className="py-24 bg-[#0E0E0E] border-y border-white/10" data-testid="michael-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <img src={MEDIA.executive} alt="Michael Brown" className="w-full aspect-[4/5] object-cover grayscale" />
              <div className="absolute -bottom-4 -right-4 bg-[#CC5500] px-6 py-4">
                <p className="font-display font-bold text-lg leading-none">Michael Brown</p>
                <p className="text-xs mt-1 text-black/80">Broker · Strategist</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>The Strategist</Overline>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">
              The mind behind the Moaglee ecosystem.
            </h2>
            <p className="text-neutral-300 mt-5 leading-relaxed">
              Michael Brown is a commercial real estate broker and business strategist helping owners, investors, and operators structure better outcomes through real estate, capital, media, and private deal flow.
            </p>
            <div className="flex gap-4 mt-8">
              <Link to="/book-call" className="btn-primary" data-testid="michael-book-btn">Book a Strategy Call</Link>
              <Link to="/michael-brown" className="btn-ghost" data-testid="michael-learn-btn">Learn More</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 9. ASPIRE */}
      <section className="py-24 max-w-7xl mx-auto px-6 text-center" data-testid="aspire-section">
        <Reveal>
          <Overline className="justify-center">Aspire · Perspire · Inspire</Overline>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight mt-4 max-w-3xl mx-auto">
            Aspire is the vision. Perspire is the work. Inspire is the impact.
          </h2>
          <p className="text-neutral-400 mt-5 max-w-2xl mx-auto">
            The mindset, book, coaching, and discipline platform connected to the Moaglee ecosystem.
          </p>
          <div className="flex justify-center gap-4 mt-8">
            <Link to="/aspire" className="btn-primary" data-testid="aspire-join-btn">Join the Coaching / Book List</Link>
            <button onClick={() => openExternal("aspire")} className="btn-ghost" data-testid="aspire-external-btn">
              AspirePerspireInspire.com <ArrowUpRight size={16} />
            </button>
          </div>
        </Reveal>
      </section>

      {/* 10. FINAL CTA */}
      <section className="relative py-28 overflow-hidden border-t border-white/10" data-testid="final-cta-section">
        <div className="absolute inset-0">
          <img src={MEDIA.heroSkyline} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-[#0A0A0A]/80" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-tight">
              You Already Built Something Valuable. Now Control More of the Upside.
            </h2>
            <div className="flex flex-wrap justify-center gap-3 mt-10">
              <Link to="/book-call" className="btn-primary" data-testid="final-book-btn">Book a Strategy Call</Link>
              <Link to="/private-network" className="btn-ghost" data-testid="final-network-btn">Join the Private Network</Link>
              <Link to="/cre" className="btn-ghost" data-testid="final-property-btn">Submit a Property</Link>
              <Link to="/capital" className="btn-ghost" data-testid="final-capital-btn">Request Capital</Link>
              <Link to="/cre" className="btn-ghost" data-testid="final-listings-btn">View CRE Listings</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}

function SplitSection({ image, overline, title, body, ctaLabel, to, external, openExternal, reverse, testid }) {
  return (
    <section className="py-24 max-w-7xl mx-auto px-6" data-testid={testid}>
      <div className={`grid lg:grid-cols-2 gap-14 items-center ${reverse ? "lg:[direction:rtl]" : ""}`}>
        <Reveal className="lg:[direction:ltr]">
          <div className="relative overflow-hidden">
            <img src={image} alt="" className="w-full aspect-[4/3] object-cover" />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:[direction:ltr]">
          <Overline>{overline}</Overline>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">{title}</h2>
          <p className="text-neutral-300 mt-5 leading-relaxed">{body}</p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link to={to} className="btn-primary">{ctaLabel}</Link>
            {external && (
              <button onClick={() => openExternal(external.key)} className="btn-ghost">
                {external.label} <ArrowUpRight size={16} />
              </button>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
